import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';
import { 
  FaCode, 
  FaMobile, 
  FaPaintBrush, 
  FaShoppingCart, 
  FaRobot, 
  FaArrowRight
} from 'react-icons/fa';
import ServiceModal from '../ServiceModal.jsx';

const Services = () => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');

  const openModal = (serviceName) => {
    setSelectedService(serviceName);
    setIsModalOpen(true);
  };

  const services = [
    {
      icon: FaMobile,
      title: 'App Development',
      description: 'Native and cross-platform mobile applications that engage users effectively.',
      subServices: [
        { name: 'iOS App' },
        { name: 'Android App' },
        { name: 'Flutter' },
        { name: 'React Native' }
      ],
      technologies: ['REACT NATIVE', 'FLUTTER', 'SWIFT'],
      delay: 0
    },
    {
      icon: FaCode,
      title: 'Web Development',
      description: 'Custom, responsive websites that drive results and user experiences.',
      subServices: [
        { name: 'React.js' },
        { name: 'PHP' },
        { name: 'Laravel' },
        { name: 'WordPress' }
      ],
      technologies: ['REACT', 'NODE.JS', 'PHP'],
      delay: 0.1
    },
    {
      icon: FaShoppingCart,
      title: 'E-commerce Solutions',
      description: 'Powerful e-commerce solutions that drive sales and conversions.',
      subServices: [
        { name: 'Magento 2' },
        { name: 'Shopify' },
        { name: 'WooCommerce' },
        { name: 'Custom Store' }
      ],
      technologies: ['SHOPIFY', 'MAGENTO', 'WOOCOMMERCE'],
      delay: 0.2
    },
    {
      icon: FaPaintBrush,
      title: 'UI/UX Design',
      description: 'Intuitive designs that enhance user satisfaction and business growth.',
      subServices: [
        { name: 'Figma Design' },
        { name: 'Adobe XD' },
        { name: 'Prototyping' },
        { name: 'User Research' }
      ],
      technologies: ['FIGMA', 'ADOBE XD', 'SKETCH'],
      delay: 0.3
    },
    {
      icon: FaRobot,
      title: 'AI & Machine Learning',
      description: 'Cutting-edge AI solutions for intelligent business insights.',
      subServices: [
        { name: 'Automation' },
        { name: 'Data Analysis' },
        { name: 'ML Models' },
        { name: 'Custom AI' }
      ],
      technologies: ['PYTHON', 'TENSORFLOW', 'PYTORCH'],
      delay: 0.4
    },
    {
      icon: FaCode,
      title: 'Digital Marketing',
      description: 'Strategic marketing to boost online presence and conversions.',
      subServices: [
        { name: 'SEO' },
        { name: 'Social Media' },
        { name: 'Google Ads' },
        { name: 'Analytics' }
      ],
      technologies: ['GOOGLE ADS', 'SEO', 'ANALYTICS'],
      delay: 0.5
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="services" className="section-padding relative overflow-hidden font-opensans bg-gray-50">
      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header - Centered */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
        >
          {/* Modern Line Icons + Title - Centered */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-0.5 bg-[#22446b]"></div>
            <span className="text-[#22446b] text-sm font-semibold uppercase tracking-wider">Featured Services</span>
            <div className="w-12 h-0.5 bg-[#22446b]"></div>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-rajdhani font-bold text-[#22446b] leading-tight max-w-4xl mx-auto mb-6">
            Advanced Solutions for Growing Your Online Business
          </h2>

          {/* Modern CTA Button - Centered */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link 
              to="/services"
              className="inline-flex items-center bg-gradient-to-r from-[#22446b] to-[#338edc] text-white font-rajdhani font-bold py-3 px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl group hover:from-[#2d5a8a] hover:to-[#22446b]"
            >
              View All Services
              <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </motion.div>

        {/* Services Grid - Clean Minimal Cards */}
        <div className="max-w-7xl mx-auto">
          <motion.div 
            ref={ref}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            {services.map((service, index) => (
              <motion.div 
                key={index}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-500"
                variants={itemVariants}
                whileHover={{ 
                  y: -5,
                  transition: { duration: 0.3 }
                }}
              >
                {/* Icon Container */}
                <div className="mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#22446b] to-[#338edc] rounded-2xl flex items-center justify-center group-hover:from-[#338edc] group-hover:to-[#22446b] transition-colors duration-300">
                    <service.icon className="text-2xl text-white" />
                  </div>
                </div>

                {/* Service Title */}
                <h3 className="text-2xl font-rajdhani font-bold text-[#22446b] mb-4">
                  {service.title}
                </h3>

                {/* Service Description */}
                <p className="text-gray-600 leading-relaxed mb-6">
                  {service.description}
                </p>

                {/* Features List */}
                <div className="space-y-3 mb-8">
                  {service.subServices.map((subService, subIndex) => (
                    <motion.div 
                      key={subIndex}
                      className="flex items-center gap-3 text-gray-700"
                      initial={{ opacity: 0, x: -10 }}
                      animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                      transition={{ delay: service.delay + subIndex * 0.1 }}
                    >
                      <div className="w-2 h-2 bg-[#106bcb] rounded-full flex-shrink-0"></div>
                      <span className="text-sm font-medium">
                        {subService.name}
                      </span>
                    </motion.div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button 
                    onClick={() => openModal(service.title)}
                    className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-[#22446b] to-[#338edc] text-white font-semibold text-sm py-2.5 px-4 rounded-lg hover:from-[#338edc] hover:to-[#22446b] transition-all duration-300 group/btn"
                  >
                    Get Quote
                    <FaArrowRight className="text-xs group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                  <Link 
                    to="/services"
                    className="flex items-center justify-center gap-2 text-[#106bcb] font-semibold text-sm py-2.5 px-4 border-2 border-[#106bcb] rounded-lg hover:border-[#22446b] hover:text-[#22446b] transition-all duration-300 group/btn"
                  >
                    Learn More
                  </Link>
                </div>

                {/* Subtle hover effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-50/0 to-gray-100/0 group-hover:from-gray-50/50 group-hover:to-gray-100/30 rounded-2xl transition-all duration-500 pointer-events-none" />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Service Modal */}
      <ServiceModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        serviceName={selectedService}
      />
    </section>
  );
};

export default Services;